import { pisti } from "@app/rules";
import { useEffect, useState } from "react";
import { cardLabel } from "./Card";
import { SWEEP_MS, type SweepStage, sweepStage, THROW_MS } from "./pistiPileParts";

type Shown = NonNullable<pisti.PistiView["pileTop"]>;

export interface PileSweep {
  stage: SweepStage;
  seat: number;
  card: Shown;
}

interface Landing {
  label: string;
  card: Shown | null;
  covered: Shown | null;
  at: number;
}

/**
 * The card the newest one is landing on, while that one is still in the air. Without it the pile
 * shows the blank edge under the top card for the length of the throw, which reads as the card in
 * the middle turning white (owner, 20 Sept 2026). Null once the card has landed, on an empty
 * table, and during a capture, where the card that was matched already lies beside the taker.
 */
export function useCoveredCard(view: pisti.PistiView): Shown | null {
  const top = view.pileTop;
  const label = top === null ? "" : cardLabel(top);
  const [landing, setLanding] = useState<Landing>({ label, card: top, covered: null, at: 0 });
  const [, redraw] = useState(0);
  if (landing.label !== label)
    setLanding({ label, card: top, covered: landing.card, at: Date.now() });
  const since = landing.covered === null ? null : landing.at;
  useEffect(() => {
    if (since === null) return;
    const timer = setTimeout(
      () => redraw((n) => n + 1),
      Math.max(0, since + THROW_MS - Date.now()) + 16,
    );
    return () => clearTimeout(timer);
  }, [since]);
  if (top === null || view.sweep !== null || landing.covered === null) return null;
  return Date.now() - landing.at < THROW_MS ? landing.covered : null;
}

interface Held {
  id: string;
  at: number;
  pauseMs: number;
  seat: number;
  card: PileSweep["card"];
}

/** The capture this view shows, as something to hold on to; null when none rests on the table. */
function captureOf(view: pisti.PistiView, now: number): Held | null {
  const { sweep, pileTop } = view;
  if (sweep === null || pileTop === null) return null;
  const pauseMs = sweep.first ? pisti.FIRST_SWEEP_PAUSE_MS : pisti.SWEEP_PAUSE_MS;
  const id = `${sweep.seat}:${cardLabel(pileTop)}:${sweep.taken}`;
  return { id, at: now, pauseMs, seat: sweep.seat, card: pileTop };
}

/**
 * What to hold after this view: a new capture from now on; one the server lifted while it still
 * rested here (a slow network, a busy tab) flies at once; anything else stays as it is.
 */
function heldAfter(held: Held | null, view: pisti.PistiView, now: number): Held | null {
  const shown = captureOf(view, now);
  if (shown !== null) return shown.id === held?.id ? held : shown;
  if (held === null) return null;
  const restMs = held.pauseMs - SWEEP_MS;
  return now - held.at < restMs ? { ...held, at: now - restMs } : held;
}

/** The capture on the table and how far along it is (see `sweepStage`), redrawn when it moves on. */
export function usePileSweep(view: pisti.PistiView): PileSweep | null {
  const [held, setHeld] = useState<Held | null>(null);
  const [, redraw] = useState(0);
  const next = heldAfter(held, view, Date.now());
  if (next !== held) setHeld(next);
  const at = held?.at ?? null;
  const pauseMs = held?.pauseMs ?? 0;
  // Keyed on the capture's clock alone: every snapshot brings a new object (the trick's lesson).
  useEffect(() => {
    if (at === null) return;
    const timers = [pauseMs - SWEEP_MS, pauseMs].map((ms) =>
      setTimeout(() => redraw((n) => n + 1), Math.max(0, at + ms - Date.now()) + 16),
    );
    return () => {
      for (const timer of timers) clearTimeout(timer);
    };
  }, [at, pauseMs]);
  if (held === null) return null;
  const stage = sweepStage(Date.now() - held.at, held.pauseMs);
  return { stage, seat: held.seat, card: held.card };
}
